import Link from "next/link";
import Image from "next/image";
import ArticleCodeBlock from "./ArticleCodeBlock";

export function renderArticleImage(line, styles, key) {
  const match = line.match(/^!\[([^\]]*)\]\((\/help\/[^\s)]+)(?: "([^"]*)")?\)$/);
  if (!match) return null;
  const [, alt, src, caption] = match;
  return (
    <figure key={key} className={styles.articleFigure}>
      <a href={src} target="_blank" rel="noreferrer" aria-label={`Open full-size image: ${alt}`}>
        <Image src={src} alt={alt} width={3024} height={1720} sizes="(max-width: 768px) 100vw, 800px" className={styles.articleImage} />
      </a>
      {caption ? <figcaption className={styles.articleCaption}>{caption}</figcaption> : null}
    </figure>
  );
}

export function findArticle(structure, articleId) {
  for (const section of structure) {
    const article = section.articles.find((item) => item.id === articleId);
    if (article) {
      return {
        ...article,
        sectionId: section.id,
        sectionTitle: section.title,
      };
    }
  }
  return null;
}

export function flattenArticles(structure) {
  return structure.flatMap((section) =>
    section.articles.map((article) => ({
      ...article,
      sectionId: section.id,
      sectionTitle: section.title,
    }))
  );
}

export function stripMarkdown(value = "") {
  return String(value)
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^#+\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function articleDescription(article) {
  const source = article?.description || article?.content || "";
  const text = stripMarkdown(source);
  if (text.length <= 155) return text;
  return `${text.slice(0, 152).trim()}...`;
}

function formatInline(text, styles) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code key={index} className={styles.inlineCode}>
          {part.slice(1, -1)}
        </code>
      );
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <Link key={index} href={linkMatch[2]} className={styles.contentLink || styles.articleLink}>
          {linkMatch[1]}
        </Link>
      );
    }
    return part;
  });
}

export function renderMarkdown(content, styles) {
  const lines = String(content || "").trim().split("\n");
  const elements = [];
  let currentList = [];
  let listType = null;
  let tableRows = [];
  let codeLines = [];
  let inCode = false;
  let codeLanguage = "";

  const flushList = () => {
    if (currentList.length === 0) return;
    const ListTag = listType === "ol" ? "ol" : "ul";
    elements.push(
      <ListTag
        key={`list-${elements.length}`}
        className={listType === "ol" ? styles.contentOrderedList || styles.list : styles.contentList || styles.list}
      >
        {currentList}
      </ListTag>
    );
    currentList = [];
    listType = null;
  };

  const flushTable = () => {
    if (tableRows.length === 0) return;
    const [headerRow, , ...bodyRows] = tableRows;
    elements.push(
      <div key={`table-${elements.length}`} className={styles.tableWrapper}>
        <table className={styles.contentTable || styles.table}>
          <thead>
            <tr>
              {headerRow.map((cell, index) => (
                <th key={index}>{formatInline(cell, styles)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex}>{formatInline(cell, styles)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    tableRows = [];
  };

  const flushCode = () => {
    if (codeLines.length === 0) return;
    elements.push(
      <ArticleCodeBlock key={`code-${elements.length}`} code={codeLines.join("\n")} language={codeLanguage} />
    );
    codeLines = [];
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (trimmed.startsWith("```")) {
      flushList();
      flushTable();
      if (inCode) flushCode();
      else codeLanguage = trimmed.slice(3).trim();
      inCode = !inCode;
      return;
    }

    if (inCode) {
      codeLines.push(line);
      return;
    }

    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      flushList();
      const cells = trimmed.slice(1, -1).split("|").map((cell) => cell.trim());
      tableRows.push(cells);
      return;
    }

    flushTable();

    const articleImage = renderArticleImage(trimmed, styles, index);
    if (articleImage) {
      flushList();
      elements.push(articleImage);
      return;
    }

    if (trimmed.startsWith("<")) {
      flushList();
      elements.push(
        <pre key={index} className={styles.codeBlock}>
          <code>{line}</code>
        </pre>
      );
      return;
    }

    if (trimmed.startsWith("# ")) {
      flushList();
      elements.push(
        <h1 key={index} className={styles.contentH1 || styles.articleTitle}>
          {trimmed.slice(2)}
        </h1>
      );
      return;
    }

    if (trimmed.startsWith("## ")) {
      flushList();
      elements.push(
        <h2 key={index} className={styles.contentH2 || styles.sectionTitle}>
          {trimmed.slice(3)}
        </h2>
      );
      return;
    }

    if (trimmed.startsWith("### ")) {
      flushList();
      elements.push(
        <h3 key={index} className={styles.contentH3 || styles.sectionTitle}>
          {trimmed.slice(4)}
        </h3>
      );
      return;
    }

    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      if (listType !== "ul") {
        flushList();
        listType = "ul";
      }
      currentList.push(<li key={index}>{formatInline(trimmed.slice(2), styles)}</li>);
      return;
    }

    if (/^\d+\.\s/.test(trimmed)) {
      if (listType !== "ol") {
        flushList();
        listType = "ol";
      }
      currentList.push(<li key={index}>{formatInline(trimmed.replace(/^\d+\.\s/, ""), styles)}</li>);
      return;
    }

    if (!trimmed) {
      flushList();
      return;
    }

    flushList();
    elements.push(
      <p key={index} className={styles.contentP || styles.articleSubtitle}>
        {formatInline(trimmed, styles)}
      </p>
    );
  });

  flushList();
  flushTable();
  flushCode();

  return elements;
}
